// LienStack — "Liens on record" on Property Detail (lien master plan WS6, #2966).
//
// Liens come from the court judgment index (claims of lien, lis pendens, NC DOR and IRS liens,
// docketed judgments) matched to this property by OWNER NAME within the county, so each row says
// how it was matched. EVIDENCE ONLY: it never changes the status badge.
//
// Absence is not "clean title". A county's coverage per lien class says whether we have actually
// checked; only a `live` class may be read as "none found". Everything else renders as
// "not checked" so a missing lien can never read as an all-clear.
//
// Renders NOTHING while loading, on error, or when the viewer is locked.

import React, { useEffect, useState } from "react";
import { Property } from "../types";
import { apiClient } from "../lib/apiClient";
import { formatDate } from "../lib/format";

export interface PropertyLien {
  lienType: string;
  holder?: string;
  amount?: number;
  filingDate?: string | null;
  courtFileNo?: string;
  status: string;
  matchTier: string;
  source: string;
}

export interface LienCoverageCell {
  lienClass: string;
  status: string;
}

interface LiensResponse {
  locked: boolean;
  liens?: PropertyLien[];
  coverage?: LienCoverageCell[];
}

const LIEN_TYPE_LABEL: Record<string, string> = {
  mechanics: "Claim of lien (contractor, supplier or HOA)",
  lis_pendens: "Lis pendens (pending lawsuit)",
  state_tax: "NC Dept. of Revenue tax lien",
  irs_federal_tax: "Federal (IRS) tax lien",
  judgment: "Docketed judgment",
  property_tax: "Delinquent property tax",
  deed_of_trust: "Deed of trust",
};

// Lien class -> display name for the coverage line.
const CLASS_LABEL: Record<string, string> = {
  L1: "property tax",
  L2: "deeds of trust",
  L3: "judgments",
  L4: "contractor liens and lis pendens",
  L5: "state and federal tax liens",
};

export function lienTypeLabel(t: string): string {
  return LIEN_TYPE_LABEL[t] || t.replace(/_/g, " ");
}

/** How the row was tied to this property, in words a reader can weigh. */
export function matchLabel(tier: string): string {
  switch (tier) {
    case "parcel":
      return "matched by parcel";
    case "exact_unique":
      return "matched by owner name (only owner with this name in the county)";
    case "name_only":
      return "name match only — verify";
    default:
      return "match unverified";
  }
}

/** Classes the county has not been fully checked for (anything other than `live`). */
export function uncheckedClasses(coverage: LienCoverageCell[] | undefined): string[] {
  const live = new Set((coverage || []).filter((c) => c.status === "live").map((c) => c.lienClass));
  return Object.keys(CLASS_LABEL)
    .filter((k) => !live.has(k))
    .map((k) => CLASS_LABEL[k]);
}

const money = (n?: number) =>
  typeof n === "number" ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "";

const LienStack: React.FC<{ property: Property }> = ({ property }) => {
  const [data, setData] = useState<LiensResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    apiClient
      .get<LiensResponse>(`/properties/${property.id}/liens`)
      .then(({ data: d }) => {
        if (!cancelled && !d.locked) setData(d);
      })
      .catch(() => {
        // Supplementary evidence, not core data: stay silent on failure.
      });
    return () => {
      cancelled = true;
    };
  }, [property.id]);

  if (!data) return null;
  const liens = data.liens || [];
  const unchecked = uncheckedClasses(data.coverage);
  if (liens.length === 0 && unchecked.length === 0) return null;

  return (
    <section className="lien-stack" aria-label="Liens on record">
      <h4 className="lien-stack-title">Liens on record</h4>
      {liens.length > 0 && (
        <ul className="lien-stack-list">
          {liens.map((l, i) => {
            const when = formatDate(l.filingDate ?? null, "");
            return (
              <li key={`${l.lienType}-${l.courtFileNo || i}-${i}`} className="lien-stack-item">
                <strong>{lienTypeLabel(l.lienType)}</strong>
                {l.status && l.status !== "active" ? ` (${l.status})` : ""}
                {when ? ` — filed ${when}` : ""}
                {l.amount
                  ? ` — ${money(l.amount)}`
                  : l.lienType === "lis_pendens"
                    ? ""
                    : " — amount not published by the clerk"}
                {l.holder ? ` — ${l.holder}` : ""}
                <span className="lien-stack-match" title="How this record was tied to the property">
                  {" "}
                  · {matchLabel(l.matchTier)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {unchecked.length > 0 && (
        <p
          className="lien-stack-coverage"
          title="Absence of a record is not proof of a clean title where we have not checked."
        >
          Not checked in this county: {unchecked.join(", ")}.
        </p>
      )}
    </section>
  );
};

export default LienStack;
